import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import BlueButton from 'components/Shared/BlueButton/BlueButton';
import ColumnPreviewTable from 'components/FieldColumns/ColumnPreviewTable';
import { FieldColumnPicker } from 'components/FieldColumns/FieldColumnPicker';
import { formatPathSegments } from 'components/FieldColumns/columnHeader';
import {
  CUSTOM_COLUMN_LISTS,
  RECORD_SOURCE_BY_TAB,
  builtinRows,
  interleaveColumns,
  toPickerColumn,
  toStoredColumn,
  buildColumnSourceTrees,
} from './columns/inspectColumns';

var TABS = ['history', 'scheduled'];
var RECORDS_TITLE_KEY = { history: 'inspect:columnsRecordFieldsHistory', scheduled: 'inspect:columnsRecordFieldsScheduled' };
var TAB_ICON = { history: 'history', scheduled: 'calendar_month' };
var TAB_LABEL_KEY = { history: 'inspect:columnsTabHistory', scheduled: 'inspect:columnsTabScheduled' };
var ERROR_KEY = /^(historyCustomColumns|scheduledCustomColumns)\[(\d+)\]/;

function listFor(config, tab) {
  var list = config[CUSTOM_COLUMN_LISTS[tab]];
  return Array.isArray(list) ? list : [];
}

var Columns = function (props) {
  var config = props.config;
  var onChange = props.onChange;
  var errors = props.errors || {};
  var t = useTranslation().t;

  var _tab = useState('history');
  var tab = _tab[0];
  var setTab = _tab[1];
  var _open = useState(false);
  var pickerOpen = _open[0];
  var setPickerOpen = _open[1];

  var trees = useMemo(function () {
    return buildColumnSourceTrees(props.settingsFields, config.recordsFieldKey);
  }, [props.settingsFields, config.recordsFieldKey]);

  var fieldsNotLoaded = !props.settingsFields || props.settingsFields.length === 0;
  var handleRemoveOrphan = function (tab, index) {
    onChange(CUSTOM_COLUMN_LISTS[tab], listFor(config, tab).filter(function (c, i) { return i !== index; }));
  };

  var handleApply = function (fields) {
    onChange(CUSTOM_COLUMN_LISTS[tab], fields.map(toStoredColumn));
    setPickerOpen(false);
  };

  var tabState = function (tab) {
    var fixed = builtinRows(tab, t);
    var rows = listFor(config, tab).map(function (c) { return toPickerColumn(c, t); });
    var previewColumns = interleaveColumns(
      fixed.map(function (r) { return Object.assign({}, r, { muted: true }); }),
      rows.filter(function (r) { return !r.orphaned; }),
    );
    return { fixed: fixed, rows: rows, previewColumns: previewColumns };
  };

  var renderCard = function (tab) {
    var st = tabState(tab);
    var count = st.previewColumns.length;
    return (
      <div>
        <div className="preview-table-picker-wrapper">
          <div className="preview-table-inline-container">
            <div className="preview-table-inline-header">
              <button type="button" className="preview-table-inline-edit" disabled={fieldsNotLoaded} onClick={function () { if (!fieldsNotLoaded) setPickerOpen(true); }}>
                <span className="material-symbols-outlined">edit_note</span>
                {t('settings:editColumns')}
              </button>
              {count > 0 && (
                <span className="preview-table-inline-count">
                  {count + ' ' + (count === 1 ? t('settings:columnSingular') : t('settings:columnPlural'))}
                </span>
              )}
            </div>
            <div className="column-picker-preview-wrapper">
              <ColumnPreviewTable columns={st.previewColumns} t={t} />
            </div>
          </div>
        </div>

        {st.rows.map(function (row, index) {
          if (!row.orphaned) return null;
          var name = formatPathSegments(row.storedPath.concat(row.storedTitle ? [row.storedTitle] : []));
          return (
            <div key={row.cuuid} className="inspect-due-banner inspect-columns-orphan">
              <span>{t('inspect:columnsOrphanMessage', { name: name })}</span>
              <BlueButton variant="secondary" label={t('inspect:columnsRemove')} onClick={function () { handleRemoveOrphan(tab, index); }} />
            </div>
          );
        })}
      </div>
    );
  };

  var errorLines = Object.keys(errors).map(function (key) {
    var match = ERROR_KEY.exec(key);
    if (!match) return null;
    var listTab = match[1] === CUSTOM_COLUMN_LISTS.history ? 'history' : 'scheduled';
    var item = listFor(config, listTab)[parseInt(match[2], 10)];
    return {
      key: key,
      text: t('inspect:columnsErrorLine', {
        tab: t(TAB_LABEL_KEY[listTab]),
        name: (item && item.fieldTitle) || '#' + (parseInt(match[2], 10) + 1),
        message: errors[key],
      }),
    };
  }).filter(Boolean);

  var active = tabState(tab);
  var sourceGroups = [
    { source: RECORD_SOURCE_BY_TAB[tab], title: t(RECORDS_TITLE_KEY[tab]), fields: trees.records },
    { source: 'slate', title: t('inspect:columnsAssetFields'), fields: trees.asset },
  ];

  return (
    <div className="inspect-section-columns">
      <div className="inspect-settings-content-header">
        <h3 className="inspect-settings-content-title">{t('inspect:sectionColumns')}</h3>
        <p className="inspect-settings-content-description">{t('inspect:columnsDescription')}</p>
        {!config.recordsFieldKey && (
          <p className="inspect-settings-content-description">{t('inspect:columnsNeedsRecordsField')}</p>
        )}
      </div>

      <div className="inspect-tabs inspect-columns-tabs" role="tablist">
        {TABS.map(function (key) {
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={'inspect-tab' + (tab === key ? ' active' : '')}
              onClick={function () { setTab(key); }}
            >
              <span className="material-symbols-outlined inspect-tab-icon">{TAB_ICON[key]}</span>
              {t(TAB_LABEL_KEY[key])}
            </button>
          );
        })}
      </div>

      {renderCard(tab)}

      {errorLines.length > 0 && (
        <div className="preview-table-error" role="alert">
          {errorLines.map(function (line) { return <div key={line.key}>{line.text}</div>; })}
        </div>
      )}

      {pickerOpen && (
        <FieldColumnPicker
          selectedFields={active.rows}
          fixedColumns={active.fixed}
          sourceGroups={sourceGroups}
          title={t('settings:editColumns')}
          applyLabel={t('inspect:columnsApply')}
          onApply={handleApply}
          onClose={function () { setPickerOpen(false); }}
          t={t}
        />
      )}
    </div>
  );
};

Columns.propTypes = {
  config: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  settingsFields: PropTypes.array,
  errors: PropTypes.object,
};

export default Columns;
